import React, { createContext, useContext, useState } from "react";
import { Snackbar, Alert, Slide } from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";

const SnackbarContext = createContext();

const getAlertStyle = (severity) => {
  switch (severity) {
    case "success":
      return { bg: "#edf7ed", color: "#2e7d32" };
    case "error":
      return { bg: "#fdecea", color: "#c62828" };
    case "warning":
      return { bg: "#fff4e5", color: "#ef6c00" };
    case "info":
      return { bg: "#e8f4fd", color: "#0277bd" };
    default:
      return { bg: "#f4f4f4", color: "#333" };
  }
};

const StyledAlert = styled(Alert)(({ severity }) => {
  const { bg, color } = getAlertStyle(severity);
  return {
    backgroundColor: bg,
    color,
    minWidth: "280px",
    maxWidth: "360px",
    padding: "10px 16px",
    borderRadius: "8px",
    fontSize: "0.95rem",
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.08)",

    "& .MuiAlert-icon": {
      color,
      fontSize: "20px",
      marginRight: "10px",
    },

    "& .MuiAlert-message": {
      padding: 0,
    },

    "& .MuiAlert-action": {
      marginLeft: "auto",
      padding: 0,
      color,
    },
  };
});

// Slide transition for animation
const SlideTransition = (props) => {
  return <Slide {...props} direction="left" />;
};

const VALID_SEVERITIES = ["success", "error", "warning", "info"];

// Snackbar Provider Component
export const SnackbarProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("info");
  const [duration, setDuration] = useState(5000);

  // Only allow known severities
  const showSnackbar = (sev = "info", msg, dur = 5000) => {
    const safeSeverity = VALID_SEVERITIES.includes(sev) ? sev : "info";
    setMessage(msg);
    setSeverity(safeSeverity);
    setDuration(dur);
    setOpen(true);
  };

  const handleClose = (event, reason) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      <Snackbar
        open={open}
        autoHideDuration={duration}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        TransitionComponent={SlideTransition}
      >
        <StyledAlert
          severity={VALID_SEVERITIES.includes(severity) ? severity : "info"}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={handleClose}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {message}
        </StyledAlert>
      </Snackbar>
      {children}
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) throw new Error("useSnackbar must be used within a SnackbarProvider");
  return context;
};

export default SnackbarProvider;
